import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { formatInteger } from '../../utils/formatters';
import type { RegistryComponentProps } from '../../pages/registry/componentRegistry';
import { getRegistryData } from '../../pages/registry/componentRegistry';
import { usePageDataContextOptional } from '../../contexts/PageDataContext';
import Modal from '../common/Modal/Modal';
import WHAMProjectionCharts from '../FisheryCharts/WHAMProjectionCharts';
import OflAbcSsbTable from '../FisheryCharts/OflAbcSsbTable';
import './StockAssessmentMetrics.css';

interface AssessmentData {
  ssb: number;
  fmsy: number;
  ssbmsy: number;
}

interface StockAssessmentMetricsData {
  stockId?: number | null;
  fisheryStockId?: number | null;
  stockUuid?: string | null;
  selectedModel?: string;
}

function getStockAssessmentData(stockId: number | null | undefined): AssessmentData | null {
  if (stockId === 10455) {
    return { ssb: 201902, fmsy: 0.028, ssbmsy: 131411 };
  }
  if (stockId === 10518) {
    return { ssb: 12293, fmsy: 0.123, ssbmsy: 25004 };
  }
  return null;
}

export default function StockAssessmentMetrics(
  props: RegistryComponentProps
): React.ReactElement {
  const pageData = usePageDataContextOptional();
  const data = getRegistryData<StockAssessmentMetricsData>(props);
  const [showModal, setShowModal] = useState(false);
  const [activeTab, setActiveTab] = useState<'charts' | 'table'>('charts');

  const pageStockId = pageData?.data?.stockId as number | null | undefined;
  const pageFisheryStockId = pageData?.data?.fisheryStockId as number | null | undefined;
  const pageStockUuid = pageData?.data?.stockUuid as string | null | undefined;
  const isWhamCompatibleStock = pageData?.data?.isWhamCompatibleStock as boolean | undefined;

  const stockId = data.stockId ?? pageStockId ?? null;
  const fisheryStockId = data.fisheryStockId ?? pageFisheryStockId ?? null;
  const stockUuid = data.stockUuid ?? pageStockUuid ?? null;

  const canShow = Boolean(isWhamCompatibleStock);
  const assessmentData = getStockAssessmentData(fisheryStockId ?? stockId);

  const handleCloseModal = () => {
    setShowModal(false);
    document.body.style.overflow = 'auto';
  };

  useEffect(() => {
    const handler = () => {
      setActiveTab('charts');
      setShowModal(true);
    };
    window.addEventListener('openStockAssessmentMetricsModal', handler);
    return () => window.removeEventListener('openStockAssessmentMetricsModal', handler);
  }, []);

  if (!canShow) return <></>;

  return (
    <div className="stock-assessment-metrics insight-card">
      <div className="insight-title-row">
        <h3 className="insight-title">Stock Assessment Metrics</h3>
      </div>
      <div className="insight-columns">
        <div
          className="insight-column clickable-column"
          style={{ marginRight: 0, cursor: 'pointer' }}
          onClick={() => {
            setActiveTab('charts');
            setShowModal(true);
          }}
        >
          <div className="assessment-metric-item">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span className="column-header" style={{ textAlign: 'left', marginBottom: 0 }}>
                SSB (Spawning Stock Biomass)
              </span>
              <span className="insight-value" style={{ textAlign: 'right' }}>
                {assessmentData ? formatInteger(assessmentData.ssb) : '---'}
              </span>
            </div>
          </div>
          <div className="assessment-metric-item">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span className="column-header" style={{ textAlign: 'left', marginBottom: 0 }}>
                F<sub>MSY</sub> (Fishing Mortality at MSY)
              </span>
              <span className="insight-value" style={{ textAlign: 'right' }}>
                {assessmentData ? assessmentData.fmsy.toFixed(3) : '---'}
              </span>
            </div>
          </div>
          <div className="assessment-metric-item">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span className="column-header" style={{ textAlign: 'left', marginBottom: 0 }}>
                SSB<sub>MSY</sub> (SSB at Maximum Sustainable Yield)
              </span>
              <span className="insight-value" style={{ textAlign: 'right' }}>
                {assessmentData ? formatInteger(assessmentData.ssbmsy) : '---'}
              </span>
            </div>
          </div>
        </div>
      </div>

      {showModal &&
        createPortal(
          <Modal
            title="Stock Assessment Details"
            onClose={handleCloseModal}
            size="extra-large"
            {...({ customMaxWidth: '95vw', customMaxHeight: '95vh' } as Record<string, unknown>)}
            bodyStyle={{ padding: '20px', overflowY: 'auto' }}
          >
            <div
              style={{
                display: 'flex',
                gap: '10px',
                marginBottom: '20px',
                borderBottom: '2px solid #e5e7eb',
                paddingBottom: '10px',
              }}
            >
              <button
                onClick={() => setActiveTab('charts')}
                style={{
                  background: activeTab === 'charts' ? '#3b82f6' : 'transparent',
                  color: activeTab === 'charts' ? 'white' : '#333',
                  border: 'none',
                  borderRadius: '4px',
                  padding: '8px 16px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '500',
                  transition: 'all 0.2s ease',
                }}
              >
                WHAM Outputs
              </button>
              <button
                onClick={() => setActiveTab('table')}
                style={{
                  background: activeTab === 'table' ? '#3b82f6' : 'transparent',
                  color: activeTab === 'table' ? 'white' : '#333',
                  border: 'none',
                  borderRadius: '4px',
                  padding: '8px 16px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '500',
                  transition: 'all 0.2s ease',
                }}
              >
                Projection Table
              </button>
            </div>
            {activeTab === 'charts' && (
              <WHAMProjectionCharts stockId={stockId ?? undefined} stockUuid={stockUuid ?? undefined} />
            )}
            {activeTab === 'table' && (
              <OflAbcSsbTable stockId={stockId ?? undefined} stockUuid={stockUuid ?? undefined} />
            )}
          </Modal>,
          document.body
        )}
    </div>
  );
}
